import jwt_decode from "jwt-decode";
import { envName, getTokenMakerUrl, isDev } from ".";

// Rate limiting: max 3 error reports per session
let errorReportCount = 0;
const MAX_ERROR_REPORTS_PER_SESSION = 3;

interface ErrorContext {
  userid?: string;
  email?: string;
  fbo_id?: string;
}

interface ErrorReportPayload {
  message: string;
  stack: string;
  componentStack?: string;
  url: string;
  userAgent: string;
  timestamp: string;
  userid?: string;
  email?: string;
  fbo_id?: string;
}

/**
 * Get user context from localStorage (JWT token and active FBO)
 * This is used when the ErrorBoundary catches an error outside of React context
 */
export function getErrorContext(): ErrorContext {
  const context: ErrorContext = {};

  try {
    // Get user info from JWT token
    const tokenKey = `airplx/${envName()}/2023-02-02/token`;
    const token = localStorage.getItem(tokenKey);
    if (token) {
      const decoded = jwt_decode<{ userid?: string; email?: string }>(token);
      context.userid = decoded.userid;
      context.email = decoded.email;
    }
  } catch (e) {
    // Token decode failed, continue without user info
    console.warn("Failed to decode JWT for error context:", e);
  }

  try {
    // Get active FBO ID from localStorage
    const fboIdKey = `airplx/${envName()}/activeFBOId`;
    const fboId = localStorage.getItem(fboIdKey);
    if (fboId) {
      // Remove quotes if stored as JSON string
      context.fbo_id = fboId.replace(/^"|"$/g, "");
    }
  } catch (e) {
    // FBO lookup failed, continue without FBO info
    console.warn("Failed to get FBO ID for error context:", e);
  }

  return context;
}

/**
 * Report an error to the backend for logging and email notification.
 * Rate limited to prevent spam in case of cascading errors.
 */
export async function reportError(
  error: Error,
  componentStack?: string
): Promise<void> {
  // Skip sending to backend in dev mode (still logs to console below)
  if (isDev()) {
    console.warn("ErrorBoundary: Skipping error report in dev mode:", error.message);
    return;
  }

  // Rate limiting
  if (errorReportCount >= MAX_ERROR_REPORTS_PER_SESSION) {
    console.warn(
      `Error reporting rate limit reached (${MAX_ERROR_REPORTS_PER_SESSION} per session). Error not reported:`,
      error.message
    );
    return;
  }

  errorReportCount++;

  // Always log to console
  console.error("Error caught by ErrorBoundary:", error);
  if (componentStack) {
    console.error("Component stack:", componentStack);
  }

  // Build error report payload
  const context = getErrorContext();
  const payload: ErrorReportPayload = {
    message: error.message || "Unknown error",
    stack: error.stack || "",
    componentStack: componentStack || undefined,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    ...context,
  };

  // Send to backend
  try {
    const baseUrl = getTokenMakerUrl();
    const response = await fetch(`${baseUrl}/error-report`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      console.warn("Failed to send error report:", response.status);
    }
  } catch (e) {
    // Don't throw if error reporting fails - that would be ironic
    console.warn("Failed to send error report:", e);
  }
}

/**
 * Reset the error report count (mainly for testing)
 */
export function resetErrorReportCount(): void {
  errorReportCount = 0;
}
